import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { BrandDto } from "@insite/client-framework/Types/ApiModels";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { css } from "styled-components";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import ADIColors from "@NA/Utils/ADIColors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { connect } from "react-redux";

interface OwnProps {
    brand: BrandDto;
    /**
     * If true, the small brand logo will be displayed.
     * If false, the brand name will be displayed instead.
     * Default value: false */
    showLogo?: boolean;
    extendedStyles?: ProductBrandStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const catalogRootPath = state.context.settings.settingsCollection.productSettings.catalogUrlPath;

    return {
        catalogRootPath: catalogRootPath.endsWith("/") ? catalogRootPath.slice(0, -1) : catalogRootPath,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductBrandStyles {
    logoImage?: LazyImageProps;
    nameLink?: LinkPresentationProps;
    nameText?: TypographyProps;
}

const nameText: TypographyProps = {
    css: css`
        width: 100%;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    color: ${ADIColors.gray4} !important;
                `,
                css`
                    color: ${ADIColors.gray4} !important;
                `,
                css`
                    color: ${ADIColors.gray4} !important;
                `,
                null,
                null,
            ])}
        ${wrapInContainerStyles}
    `,
    size: "13px",
    weight: 700,
};

export const productBrandStyles: ProductBrandStyles = {
    nameText,
    nameLink: {
        css: css`
            width: auto;
            font-weight: 700px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        text-decoration: none !important;
                    `,
                    css`
                        text-decoration: none !important;
                    `,
                    css`
                        text-decoration: none !important;
                    `,
                    null,
                    null,
                ])}
        `,
        typographyProps: nameText,
    },
};

const ProductBrand: FC<Props> = ({ brand, showLogo = false, extendedStyles, catalogRootPath }) => {
    if (!brand) {
        return null;
    }

    const getBrandUrl= ()=>{
        if(brand.detailPagePath.toLowerCase().startsWith('/catalog'))
             {return brand.detailPagePath}            
        return catalogRootPath + brand.detailPagePath;
    }

    const [styles] = React.useState(() => mergeToNew(productBrandStyles, extendedStyles));

    let brandDisplay;
    if (showLogo && brand.logoSmallImagePath) {
        const altText = brand.logoImageAltText || brand.name;
        brandDisplay = <LazyImage {...styles.logoImage} src={brand.logoSmallImagePath} altText={altText} />;
    } else {
        brandDisplay = brand.detailPagePath ? (
            brand.name
        ) : (
            <Typography {...styles.nameText} data-test-selector="brandName">
                {brand.name}
            </Typography>
        );
    }

    return (brand.detailPagePath && typeof window !== "undefined" && window.location.pathname !== "/Checkout/ReviewAndPay") ? (
        <Link {...styles.nameLink} href={getBrandUrl()} data-test-selector="brandLink">
            {brandDisplay}
        </Link>
    ) : (
        <>{brandDisplay}</>
    );
};

export default connect(mapStateToProps)(ProductBrand);
